import { Button, Group, Stack, Text, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconPlus } from '@tabler/icons-react'
import { useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { DeploymentAlertSetting } from '@/types/deploymentAlert'
import { Device } from '@/types/device'
import { NotificationRuleCard } from './NotificationRuleCard'
import {
  CreatedRuleAssignment,
  NotificationsFormValues,
  SavedNotificationRuleFormValues,
  createEmptyRule,
  toFormValues
} from './notificationRules'

type NotificationRulesFormProps = {
  alertSettings: DeploymentAlertSetting[]
  devices: Device[]
  isLoadingDevices?: boolean
  isSaving?: boolean
  canManage?: boolean
  onDeleteSavedRule: (
    rule: SavedNotificationRuleFormValues,
    removeFromForm: () => void
  ) => void
  onSubmit: (
    values: NotificationsFormValues,
    assignCreatedIds: (assignments: CreatedRuleAssignment[]) => void
  ) => void
}

export const NotificationRulesForm = ({
  alertSettings,
  devices,
  isLoadingDevices,
  isSaving,
  canManage,
  onDeleteSavedRule,
  onSubmit
}: NotificationRulesFormProps) => {
  const intl = useIntl()
  const blockingDrafts = useRef(new Set<string>())

  const handleBlockingDraftChange = (key: string, blocking: boolean) => {
    if (blocking) {
      blockingDrafts.current.add(key)
    } else {
      blockingDrafts.current.delete(key)
    }
  }

  const form = useForm<NotificationsFormValues>({
    initialValues: toFormValues(alertSettings),
    validate: {
      rules: {
        device_ids: (value: string[]) =>
          value.length === 0
            ? intl.formatMessage({
                id: 'workspace.notifications.devices.required'
              })
            : null,
        // The server rejects a rule with no recipient at all, so the check is
        // reported on the emails field rather than per-field.
        emails: (_value, values, path) => {
          const index = Number(path.split('.')[1])
          const rule = values.rules[index]

          return rule.emails.length === 0 && rule.chat_webhooks.length === 0
            ? intl.formatMessage({
                id: 'workspace.notifications.recipients.required'
              })
            : null
        }
      }
    }
  })

  const handleAddRule = () => form.insertListItem('rules', createEmptyRule())

  // A rule that was never saved exists only on screen, so it just goes; a saved
  // one is a server record and gets the confirm-then-delete treatment.
  const handleRemoveRule = (index: number) => {
    const rule = form.values.rules[index]

    if (rule.id) {
      onDeleteSavedRule({ ...rule, id: rule.id }, () => {
        const currentIndex = form.values.rules.findIndex(
          (current) => current.id === rule.id
        )

        if (currentIndex >= 0) {
          form.removeListItem('rules', currentIndex)
        }
      })
      return
    }

    form.removeListItem('rules', index)
  }

  const assignCreatedIds = (assignments: CreatedRuleAssignment[]) => {
    assignments.forEach(({ uid, id }) => {
      const index = form.values.rules.findIndex((rule) => rule.uid === uid)

      if (index >= 0) {
        form.setFieldValue(`rules.${index}.id`, id)
      }
    })
  }

  return (
    <form
      noValidate
      onSubmit={form.onSubmit((values) => {
        if (blockingDrafts.current.size > 0) {
          return
        }

        onSubmit(values, assignCreatedIds)
      })}
    >
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={3}>
              <FormattedMessage id="workspace.notifications.title" />
            </Title>

            <Text size="sm" c="dimmed">
              <FormattedMessage id="workspace.notifications.description" />
            </Text>
          </Stack>

          {canManage && (
            <Button
              variant="default"
              leftSection={<IconPlus size={16} />}
              disabled={isSaving}
              onClick={handleAddRule}
            >
              <FormattedMessage id="workspace.notifications.addRule" />
            </Button>
          )}
        </Group>

        <Stack gap="md">
          {form.values.rules.length === 0 && (
            <PlatformAlert variant="info">
              <FormattedMessage id="workspace.notifications.empty" />
            </PlatformAlert>
          )}

          {form.values.rules.map((rule, index) => (
            <NotificationRuleCard
              key={rule.uid}
              index={index}
              form={form}
              devices={devices}
              isLoadingDevices={isLoadingDevices}
              canRemove={canManage && !isSaving}
              disabled={!canManage || isSaving}
              onRemove={() => handleRemoveRule(index)}
              onBlockingDraftChange={handleBlockingDraftChange}
            />
          ))}
        </Stack>

        {canManage && (
          <Group justify="end">
            <Button miw={180} type="submit" loading={isSaving}>
              <FormattedMessage id="workspace.notifications.save" />
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  )
}
